import { useParams, Link, useNavigate } from 'react-router-dom'
import { useVenue, useUpdateVenue } from '@/hooks/useVenues'
import { useAuth } from '@/contexts/AuthContext'
import { useToast } from '@/hooks/use-toast'
import { QualityDropdown } from '@/components/shared/QualityDropdown'
import { WikidataLink } from '@/components/shared/WikidataLink'
import { InlineEdit } from '@/components/shared/InlineEdit'
import { Button } from '@brent/core-ui/ui'
import { Badge } from '@brent/core-ui/ui'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@brent/core-ui/ui'
import { ArrowLeft } from 'lucide-react'
import type { QualityScore } from '@brent/shared/types'

export function VenueDetail() {
  const { id } = useParams()
  const venueId = Number(id)
  const navigate = useNavigate()
  const { data, isLoading, error } = useVenue(venueId)
  const { canEdit } = useAuth()
  const updateVenue = useUpdateVenue()
  const { toast } = useToast()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">Loading venue...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6">
        <h2 className="font-semibold text-destructive">Failed to load venue</h2>
        <p className="mt-1 text-sm text-muted-foreground">{error.message}</p>
      </div>
    )
  }

  if (!data) return null

  const { venue, concerts = [] } = data

  const handleQualityChange = async (qualityScore: QualityScore) => {
    try {
      await updateVenue.mutateAsync({
        id: venue.id,
        input: { quality_score: qualityScore },
      })
      toast({ title: 'Updated', description: `Quality set to ${qualityScore}` })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to update quality',
      })
    }
  }

  const handleQidSave = async (qid: string) => {
    try {
      const result = await updateVenue.mutateAsync({
        id: venue.id,
        input: { wikidata_id: qid },
      })
      if (result.enriched) {
        toast({
          title: 'Enriched',
          description: `Matched to ${result.enriched.label}`,
        })
      } else if (result.cleared) {
        toast({ title: 'Cleared', description: 'Wikidata ID removed' })
      }
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to update QID',
      })
    }
  }

  return (
    <div className="space-y-8">
      <div>
        <Link to="/venues">
          <Button variant="ghost" size="sm" className="mb-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to venues
          </Button>
        </Link>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{venue.name}</h1>
          </div>
          <div className="flex items-center gap-3">
            {canEdit ? (
              <QualityDropdown
                value={venue.quality_score}
                onChange={handleQualityChange}
                disabled={updateVenue.isPending}
              />
            ) : (
              <Badge variant="secondary" className="capitalize">
                {venue.quality_score.replace('_', ' ')}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Metadata */}
      <section className="rounded-lg border bg-card p-6">
        <h2 className="text-lg font-semibold mb-4">Details</h2>
        <dl className="grid gap-4 sm:grid-cols-2">
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Wikidata ID</dt>
            <dd className="mt-1">
              {canEdit ? (
                <InlineEdit
                  value={venue.wikidata_id || ''}
                  onSave={handleQidSave}
                  placeholder="Add QID (e.g. Q1234)"
                />
              ) : (
                <WikidataLink qid={venue.wikidata_id} />
              )}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Wikidata Label</dt>
            <dd className="mt-1 text-sm">{venue.wikidata_label || '-'}</dd>
          </div>
          {venue.notes && (
            <div className="sm:col-span-2">
              <dt className="text-sm font-medium text-muted-foreground">Notes</dt>
              <dd className="mt-1 text-sm">{venue.notes}</dd>
            </div>
          )}
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Concerts</dt>
            <dd className="mt-1 text-sm tabular-nums">{concerts.length}</dd>
          </div>
        </dl>
      </section>

      {/* Concert history */}
      <section className="space-y-4">
        <h2 className="text-lg font-semibold">
          Concert History{' '}
          <span className="text-muted-foreground font-normal">({concerts.length})</span>
        </h2>
        {concerts.length > 0 ? (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Ensemble</TableHead>
                  <TableHead className="text-right">Programme Items</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {concerts.map((concert) => (
                  <TableRow
                    key={concert.concert_id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/concerts/${concert.concert_id}`)}
                  >
                    <TableCell className="whitespace-nowrap">
                      {concert.date_text || `Concert #${concert.concert_id}`}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {concert.ensemble_name || '-'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground text-right tabular-nums">
                      {concert.programme_count}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No concerts recorded at this venue.</p>
        )}
      </section>
    </div>
  )
}
